import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isOtpRequired } from '@gwc/shared';
import { useAuth } from '../auth/AuthProvider';
import { BRAND } from '../brand';
import { useTheme } from '../theme/ThemeProvider';
import { formatAuthError } from '../lib/authErrors';
import { ApiError } from '../lib/api';

type Step = 'credentials' | 'otp';

export function Login() {
  const { login, verifyOtp, setup, needsSetup } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (needsSetup) {
        await setup({ email, password, firstName, lastName });
        navigate('/dashboard');
        return;
      }
      const res = await login(email, password);
      if (isOtpRequired(res)) {
        setChallengeId(res.challengeId);
        setEmailMasked(res.emailMasked);
        setOtp('');
        setStep('otp');
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(formatAuthError(err, needsSetup));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitOtp(e: FormEvent) {
    e.preventDefault();
    if (otp.trim().length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await verifyOtp(challengeId, otp.trim());
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not verify that code.');
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'otp') {
    return (
      <div className="mx-auto max-w-sm">
        <img
          src={theme === 'dark' ? BRAND.logoSrc : BRAND.logoOriginal}
          alt={BRAND.name}
          className="mx-auto mb-6 w-full max-w-xs"
        />
        <h1 className="mb-2 text-center font-display text-3xl text-cream-50">Check your email</h1>
        <p className="mb-6 text-center text-sm text-cream-300/70">
          We sent a 6-digit code to {emailMasked || 'your email'}. Enter it below to verify your
          address and finish signing in.
        </p>
        <form onSubmit={onSubmitOtp} className="card space-y-4">
          {error && (
            <p className="rounded border border-wine-500/50 bg-wine-900/40 px-3 py-2 text-sm text-wine-100">
              {error}
            </p>
          )}
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
              Verification code
            </label>
            <input
              className="field text-center font-mono text-lg tracking-[0.35em]"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
            />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? 'Verifying…' : 'Verify & continue'}
          </button>
          <button
            type="button"
            className="btn-ghost w-full text-sm"
            disabled={submitting}
            onClick={() => {
              setStep('credentials');
              setOtp('');
              setError(null);
            }}
          >
            Back to sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm">
      <img
        src={theme === 'dark' ? BRAND.logoSrc : BRAND.logoOriginal}
        alt={BRAND.name}
        className="mx-auto mb-6 w-full max-w-xs"
      />
      <h1 className="mb-2 text-center font-display text-3xl text-cream-50">
        {needsSetup ? 'Welcome — set up your club' : 'Member Log in'}
      </h1>
      {needsSetup && (
        <p className="mb-6 text-center text-sm text-cream-300/70">
          Create the first administrator account. You'll be able to invite everyone else from inside.
        </p>
      )}
      <form onSubmit={onSubmitCredentials} className="card space-y-4">
        {error && (
          <p className="rounded border border-wine-500/50 bg-wine-900/40 px-3 py-2 text-sm text-wine-100">
            {error}
          </p>
        )}

        {needsSetup && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
                First name
              </label>
              <input
                className="field"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
                Last name
              </label>
              <input
                className="field"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </div>
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Email</label>
          <input
            type="email"
            className="field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
            Password
          </label>
          <input
            type="password"
            className="field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={needsSetup ? 'new-password' : 'current-password'}
            minLength={needsSetup ? 10 : undefined}
            required
          />
          {needsSetup && (
            <p className="mt-1 text-xs text-cream-300/50">At least 10 characters.</p>
          )}
        </div>
        <button type="submit" className="btn-primary w-full" disabled={submitting}>
          {submitting
            ? needsSetup
              ? 'Creating…'
              : 'Signing in…'
            : needsSetup
              ? 'Create admin & enter'
              : 'Log in'}
        </button>
      </form>
      {!needsSetup && (
        <p className="mt-4 text-center text-xs text-cream-300/50">
          <Link to="/forgot-password" className="text-gold-200 hover:underline">
            Forgot password?
          </Link>
          {' · '}
          <Link to="/help/4-getting-in-accounts-and-signing-in" className="text-gold-200 hover:underline">
            Help signing in
          </Link>
          {' · '}
          Membership is by invitation.
        </p>
      )}
    </div>
  );
}
